import {
  Avatar,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Skeleton,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import React, { useEffect } from 'react';
import { TaskTypeSvg } from './task-type-svg';
import { toCamelCase } from './therapist-task-list';
import { DotIcon } from 'src/pages/therapist-dashboard/todays-activity/task-deatails';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { LoadingButton } from '@mui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { editSplitTaskMinAction } from 'src/redux/actions/schedularTherapistFilterAction';
import useCurrentUser from 'src/hooks/use-current-user';

type Props = {
  handleContinueSplitMin?: (arg: any) => void;
  sourceOfSplitMin: any,
  handleCancelSplitMin: () => void;
  patient: any;
};

const MinIcon = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
    >
      <g clip-path="url(#clip0_45444_17123)">
        <path
          d="M9.99996 5.0013V10.0013L13.3333 11.668M18.3333 10.0013C18.3333 14.6037 14.6023 18.3346 9.99996 18.3346C5.39759 18.3346 1.66663 14.6037 1.66663 10.0013C1.66663 5.39893 5.39759 1.66797 9.99996 1.66797C14.6023 1.66797 18.3333 5.39893 18.3333 10.0013Z"
          stroke="#6C737F"
          stroke-width="1.4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_45444_17123">
          <rect
            width="20"
            height="20"
            fill="white"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

const TaskSplitMin = (props: Props) => {
  const dispatch = useDispatch()
  const [providerId] = useCurrentUser(['providerId']);
  const { handleContinueSplitMin, handleCancelSplitMin, patient, sourceOfSplitMin } = props;
  const splitMin = useSelector((state: any) => state.SplitTaskMinReducer);
  const getSplitMinRes = useSelector((state: any) => state.getSplitTaskReducer);
  const editSplitTaskMin = useSelector((state: any) => state.editSplitTaskMinReducer);

  useEffect(() => {
    if (sourceOfSplitMin !== 'EDIT_SPLIT_MIN') return
    if (!getSplitMinRes?.getSplitTask && getSplitMinRes?.response === null) return
    if (!getSplitMinRes?.getSplitTask && getSplitMinRes?.response !== null) return
    formik.setValues({
      task1Minutes: String(getSplitMinRes?.response?.data?.[0]?.number_of_minutes),
      task2Minutes: String(getSplitMinRes.response?.data?.[1]?.number_of_minutes),
    });
  }, [getSplitMinRes]);

  const totalMinutes =
    sourceOfSplitMin === 'EDIT_SPLIT_MIN'
      ? Number(getSplitMinRes.response?.data?.[1]?.number_of_minutes) +
      Number(getSplitMinRes?.response?.data?.[0]?.number_of_minutes)
      : patient?.numberOfMinutes || 0;


  const formik = useFormik({
    initialValues: {
      task1Minutes: '',
      task2Minutes: '',
    },
    validationSchema: Yup.object({
      task1Minutes: Yup.number()
        .min(
          sourceOfSplitMin !== 'EDIT_SPLIT_MIN' ? 1 : 0,
          sourceOfSplitMin !== 'EDIT_SPLIT_MIN' ? 'Minutes are not allowed to split into zero' : 'Minutes cannot be negative'
        )
        .max(totalMinutes, `Cannot exceed ${totalMinutes} minutes`)
        .required('Required'),
      task2Minutes: Yup.number()
        .min(
          sourceOfSplitMin !== 'EDIT_SPLIT_MIN' ? 1 : 0,
          sourceOfSplitMin !== 'EDIT_SPLIT_MIN' ? 'Minutes are not allowed to split into zero' : 'Minutes cannot be negative'
        )
        .max(totalMinutes, `Cannot exceed ${totalMinutes} minutes`)
        .required('Required'),
    }),
    onSubmit: (values) => {
      if (sourceOfSplitMin === 'EDIT_SPLIT_MIN') {
        dispatch(editSplitTaskMinAction({
          providerId,
          data: {
            firstTaskUuid: getSplitMinRes.response?.data?.[0]?.uuid,
            secondTaskUuid: getSplitMinRes.response?.data?.[1]?.uuid,
            firstTaskMinutes: parseInt(values.task1Minutes),
            secondTaskMinutes: parseInt(values.task2Minutes)
          }
        }))
        return
      }
      handleContinueSplitMin && handleContinueSplitMin({
        task1Minutes: parseInt(values.task1Minutes),
        task2Minutes: parseInt(values.task2Minutes),
        uuid: patient?.uuid,
      });
    },
  });

  // Handle task1Minutes change
  const handleTask1Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const task1Value = e.target.value;
    const task1Minutes = task1Value === '' ? 0 : parseInt(task1Value);

    if (task1Minutes <= totalMinutes) {
      formik.setValues({
        task1Minutes: task1Value,
        task2Minutes: String(totalMinutes - task1Minutes),
      });
    }
  };

  // Handle task2Minutes change
  const handleTask2Change = (e: React.ChangeEvent<HTMLInputElement>) => {
    const task2Value = e.target.value;
    const task2Minutes = task2Value === '' ? 0 : parseInt(task2Value);

    if (task2Minutes <= totalMinutes) {
      formik.setValues({
        task1Minutes: String(totalMinutes - task2Minutes),
        task2Minutes: task2Value,
      });
    }
  };

  return (
    <>
      {getSplitMinRes?.loading ? (
        <Grid
          width={600}
          container
          flexDirection={'column'}
          gap={'5px'}
        >
          <form>
            <DialogTitle
              sx={{ m: 0, p: 2 }}
              id="customized-dialog-title"
            >
              <Stack flexDirection={'column'}>
                <Skeleton
                  width={180}
                  height={32}
                  variant="text"
                />
                <Stack
                  flexDirection={'row'}
                  alignItems={'center'}
                  gap={2}
                  mt={1}
                >
                  <Stack
                    flexDirection={'row'}
                    gap={1}
                  >
                    <Skeleton
                      variant="circular"
                      width={24}
                      height={24}
                    />
                    <Skeleton
                      width={120}
                      height={24}
                      variant="text"
                    />
                  </Stack>
                  <Stack>
                    <Skeleton
                      variant="circular"
                      width={8}
                      height={8}
                    />
                  </Stack>
                  <Stack
                    flexDirection={'row'}
                    gap={1}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={24}
                      height={24}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <Skeleton
                        width={80}
                        height={24}
                        variant="text"
                      />
                    </Box>
                  </Stack>
                  <Stack>
                    <Skeleton
                      variant="circular"
                      width={8}
                      height={8}
                    />
                  </Stack>
                  <Stack
                    gap={1}
                    flexDirection={'row'}
                  >
                    <Skeleton
                      variant="rectangular"
                      width={20}
                      height={20}
                    />
                    <Skeleton
                      width={60}
                      height={24}
                      variant="text"
                    />
                  </Stack>
                </Stack>
              </Stack>
            </DialogTitle>
            <DialogContent sx={{ paddingBottom: '0px !important', mt: 2 }}>
              <Grid
                container
                wrap="nowrap"
                spacing={2}
              >
                <Grid item>
                  <Skeleton
                    width={80}
                    height={20}
                    variant="text"
                  />
                  <Skeleton
                    width={180}
                    height={56}
                    variant="rectangular"
                    sx={{ borderRadius: 1, mt: 1 }}
                  />
                </Grid>
                <Grid item>
                  <Skeleton
                    width={80}
                    height={20}
                    variant="text"
                  />
                  <Skeleton
                    width={180}
                    height={56}
                    variant="rectangular"
                    sx={{ borderRadius: 1, mt: 1 }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ mt: 2 }}>
              <Skeleton
                width={90}
                height={36}
                variant="rectangular"
                sx={{ borderRadius: 1 }}
              />
              <Skeleton
                width={120}
                height={36}
                variant="rectangular"
                sx={{ borderRadius: 1 }}
              />
            </DialogActions>
          </form>
        </Grid>
      ) : (
          <Grid
            width={600}
            container
            flexDirection={'column'}
            gap={'5px'}
          >
            <form onSubmit={formik.handleSubmit}>
              <DialogTitle
                sx={{ m: 0, p: 2 }}
                id="customized-dialog-title"
              >
                <Stack flexDirection={'column'}>
                  <Typography variant="h6">Split Task Minutes</Typography>
                  <Stack
                    flexDirection={'row'}
                    alignItems={'center'}
                    gap={2}
                    mt={1}
                  >
                    <Stack
                      flexDirection={'row'}
                      gap={1}
                    >
                      <Avatar sx={{ width: 24, height: 24 }}>{`${patient?.patientLastname?.at(
                        0
                      )}${patient?.patientFirstname?.at(0)}   `}</Avatar>
                      <Typography
                        sx={{ color: '#6C737F' }}
                        variant="subtitle2"
                      >
                        {`${patient?.patientLastname} ${patient?.patientFirstname}`}
                      </Typography>
                    </Stack>
                    <Stack>
                      <DotIcon />
                    </Stack>
                    <Stack
                      flexDirection={'row'}
                      gap={1}
                    >
                      <TaskTypeSvg taskType={patient?.tasksType?.replace('_', ' ')} />
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#6C737F', fontWeight: 500 }}
                        >
                          {toCamelCase(patient?.tasksType)}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack>
                      <DotIcon />
                    </Stack>
                    <Stack
                      gap={1}
                      flexDirection={'row'}
                    >
                      <MinIcon />
                      <Typography
                        sx={{ color: '#6C737F', fontWeight: 500 }}
                        variant="subtitle2"
                      >
                        {totalMinutes} mins.
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </DialogTitle>
              <DialogContent
                sx={{
                  paddingBottom: '0px !important',
                  mt: 2,
                }}
              >
                <Grid
                  container
                  wrap="nowrap"
                  spacing={2}
                >
                  <Grid item>
                    <Typography variant="overline">{`Split 1${patient?.tasksType === 'THERAPY' && patient?.scheduleItemType !== 'APPOINTMENT' ? '' : ' (Main)'}`}</Typography>
                    <TextField
                      label="Number of Minutes"
                      fullWidth
                      name="task1Minutes"
                      value={formik.values.task1Minutes}
                      onChange={handleTask1Change}
                      error={formik.touched.task1Minutes && Boolean(formik.errors.task1Minutes)}
                      helperText={formik.touched.task1Minutes && formik.errors.task1Minutes}
                      type="number"
                    />
                  </Grid>
                  <Grid item>
                    <Typography variant="overline">{`Split 2${patient?.tasksType === 'THERAPY' && patient?.scheduleItemType !== 'APPOINTMENT' ? '' : ' (Secondary)'}`}</Typography>
                    <TextField
                      label="Number of Minutes"
                      fullWidth
                      name="task2Minutes"
                      value={formik.values.task2Minutes}
                      onChange={handleTask2Change}
                      error={formik.touched.task2Minutes && Boolean(formik.errors.task2Minutes)}
                      helperText={formik.touched.task2Minutes && formik.errors.task2Minutes}
                      type="number"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions sx={{ mt: 2 }}>
                <Button
                  autoFocus
                  onClick={handleCancelSplitMin}
                >
                  Cancel
                </Button>
                <LoadingButton
                  autoFocus
                  variant="contained"
                  type="submit"
                  loading={splitMin?.loading || editSplitTaskMin?.loading}
                  disabled={
                    !formik.isValid ||
                    formik.values.task1Minutes === '' ||
                    formik.values.task2Minutes === ''
                  }
                >
                  Split Task
                </LoadingButton>
              </DialogActions>
            </form>
          </Grid>
      )}
    </>
  );
};

export default TaskSplitMin;
